import { SeoFooterLink } from 'components/SeoFooterLink';
import { FollowSearchResultsNavigation } from 'components/FollowSearchResultsNavigation';
import { ModalMasterData } from 'components/SearchSideBar/BasicInfo/types';
import { useSelector } from 'react-redux';
import {
  MetaDataSelector,
  validationMessagesSelector,
} from 'redux/features/jobList';
import { labelTextSelector } from '@doda/common/src/redux/features/searchConditionLabel';
import { useMemo, FC } from 'react';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { loginStatusSelector } from '@doda/common/src/redux/features/memberInfo';
import { useAnalyticsSetup } from '@doda/common/src/hooks/useAnalytics';
import { useCmsMemberInfo } from '@doda/common/src/hooks/useCmsMemberInfo';
import { LinkBox } from '@doda/common/src/logics/seoFooterLinkData/createSeoFooterLink';
import { JobList } from '../../components/JobList';
import styles from './index.module.scss';
import { SearchSidebar } from '../../components/SearchSidebar';
import { Breadcrumb } from '../../components/Breadcrumb';
import { BodyHeader } from '../../components/BodyHeader';
import { MetaTag } from '../../components/MetaTag';
import { SideBarProps } from '../../components/SearchSidebar/types';
import { useCareerRecommendJobListAnalytics } from '../../hooks/useCareerRecommendJobListAnalytics';
import { ValidationMessages } from '../../components/ValidationMessages';
import { calcMaxPage } from '../../logics/calcMaxPage';
import { ListControls } from '../../components/ListControls';
import { AgentServiceAppeal } from '../../components/AgentServiceAppeal';

/**
 * コンポーネントのプロパティを定義するインターフェース
 */
export interface CareerRecommendJobListScreenProps {
  /** エージェントサービスバナー表示フラグ */
  isShowAgentService: boolean;
  /** モーダルに表示するマスター情報 */
  modalMasterData: ModalMasterData;
  /** サイドバープロパティ */
  sideBarProps: SideBarProps;
  /** SEOフッターリンク */
  seoFooterLink: LinkBox[];
  /** 現在のページ番号 */
  currentPage: number;
}

/**
 * CareerRecommendJobListScreenコンポーネント
 *
 * @param {CareerRecommendJobListScreenProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} JSX要素
 */
export const CareerRecommendJobListScreen: FC<
  CareerRecommendJobListScreenProps
> = ({
  isShowAgentService,
  modalMasterData,
  sideBarProps,
  seoFooterLink,
  currentPage,
}) => {
  useAnalyticsSetup({ isNeedNormalize: false });
  useCmsMemberInfo('pcHeader');
  useCareerRecommendJobListAnalytics();

  const jobListMeta = useSelector(MetaDataSelector);
  const searchConditionLabel = useSelector(labelTextSelector);
  const isLogin = useSelector(loginStatusSelector);

  const { so } = useSelector(appQueryParamsSelector);
  const maxPage = useMemo(
    () => calcMaxPage({ jobListMeta, so }),
    [jobListMeta, so]
  );

  const { isVisibleValidationMessages, validationMessages } = useSelector(
    validationMessagesSelector
  );

  return (
    <>
      <FollowSearchResultsNavigation
        loginStatus={isLogin}
        totalCount={jobListMeta?.totalCount ?? 0}
        resultCount={jobListMeta?.resultCount ?? 0}
        offset={jobListMeta?.offset ?? 0}
      />
      <MetaTag />
      <Breadcrumb />
      <div className={styles.careerRecommendJobList__main}>
        <div className={styles.careerRecommendJobList__sidebar}>
          <SearchSidebar
            searchConditionLabel={searchConditionLabel}
            modalMasterData={modalMasterData}
            sideBarProps={sideBarProps}
          />
        </div>
        <div className={styles.careerRecommendJobList__contents}>
          <BodyHeader />
          {isVisibleValidationMessages ? (
            <ValidationMessages validationMessages={validationMessages} />
          ) : (
            <>
              <ListControls maxPage={maxPage} />
              <JobList maxPage={maxPage} currentPage={currentPage} />
              <AgentServiceAppeal isShowAgentService={isShowAgentService} />
              <div
                className={styles.careerRecommendJobList__seoFooterLinkWrapper}
              >
                {seoFooterLink.map(({ boxName, boxLinks }) => (
                  <SeoFooterLink
                    title={boxName}
                    listItems={boxLinks}
                    key={boxName}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
